import { useState, useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { buildProgrammeIdentifier } from '@/utils/programmeIdentifier'
import type { TvProgramme } from '@/types/tvTypes'

function formatTime(date: Date): string {
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

function buildInitialThreadBody(programme: TvProgramme, channelName?: string): string {
  const timeStr = `${formatTime(programme.start)}–${formatTime(programme.stop)}`
  const context = channelName
    ? `Discussing: ${programme.title} (${channelName}, ${timeStr}).`
    : `Discussing: ${programme.title} (${timeStr}).`
  const desc = programme.desc?.trim() || 'Discuss this programme below.'
  const body = `${context}\n\n${desc}`
  return body.length >= 10 ? body : `${body}\n\nDiscuss this programme below.`
}

interface NewCommentFromTvModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (groupId: string, threadId: string) => void
  programme: TvProgramme
  channelName?: string
  /** When false (e.g. user chose "No, create New" from Add to existing?), thread title and body start blank. Default true. */
  prefillThread?: boolean
}

interface GroupMember {
  group_id: string
  groups: { id: string; name: string; description: string | null; group_type?: string | null }
}

export default function NewCommentFromTvModal({
  isOpen,
  onClose,
  onSuccess,
  programme,
  channelName,
  prefillThread = true,
}: NewCommentFromTvModalProps) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null)
  const [groupName, setGroupName] = useState('')
  const [groupDescription, setGroupDescription] = useState('')
  const [threadTitle, setThreadTitle] = useState(prefillThread ? programme.title : '')
  const [threadBody, setThreadBody] = useState(prefillThread ? buildInitialThreadBody(programme, channelName) : '')
  const [commentBody, setCommentBody] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const firstInputRef = useRef<HTMLInputElement>(null)

  const programmeIdentifier = buildProgrammeIdentifier(programme)

  const { data: userGroups } = useQuery({
    queryKey: ['user-groups', user?.id],
    queryFn: async () => {
      if (!user) return []
      const { data, error: queryError } = await supabase
        .from('group_members')
        .select('group_id, groups(id, name, description, group_type)')
        .eq('user_id', user.id)
        .order('joined_at', { ascending: false })
      if (queryError) throw queryError
      const transformed = (data || []).map((m: { group_id: string; groups: unknown }) => ({
        group_id: m.group_id,
        groups: Array.isArray(m.groups) ? m.groups[0] : m.groups,
      })) as GroupMember[]
      return transformed.filter((m) => {
        const g = m.groups
        return !g?.group_type || (g.group_type !== 'broadcaster' && g.group_type !== 'ad-creator')
      })
    },
    enabled: isOpen && !!user,
  })

  const defaultGroupName = channelName ? `${channelName} – programmes` : 'New group'

  useEffect(() => {
    if (isOpen) {
      setThreadTitle(prefillThread ? programme.title : '')
      setThreadBody(prefillThread ? buildInitialThreadBody(programme, channelName) : '')
      setGroupName(defaultGroupName)
      setGroupDescription(channelName ? `Discussion for ${channelName} programmes` : '')
      setCommentBody('')
      setError(null)
      // Don't reset selectedGroupId here – let the pre-select effect set it when userGroups is available,
      // so we never briefly show "Create new group" and then create a duplicate when userGroups load after open.
    }
  }, [isOpen, programme, channelName, prefillThread, defaultGroupName])

  // Pre-select existing group for this channel so we don't create a duplicate (e.g. second programme on same channel).
  // Runs when userGroups is available; sets selectedGroupId to matching group or null so submit uses the right path.
  useEffect(() => {
    if (!isOpen) return
    if (userGroups === undefined) return // still loading – don't clear selection
    if (!userGroups.length) {
      setSelectedGroupId(null)
      return
    }
    const matching = userGroups.find((m) => m.groups.name === defaultGroupName)
    setSelectedGroupId(matching ? matching.groups.id : null)
  }, [isOpen, userGroups, defaultGroupName])

  useEffect(() => {
    if (isOpen) firstInputRef.current?.focus()
  }, [isOpen])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) onClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose, isSubmitting])

  const createAll = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Not authenticated')

      let groupId: string

      if (selectedGroupId) {
        groupId = selectedGroupId
      } else {
        if (!groupName.trim() || groupName.trim().length < 3) throw new Error('Group name must be at least 3 characters')
        const { data: group, error: groupError } = await supabase
          .from('groups')
          .insert({ name: groupName.trim(), description: groupDescription.trim() || null, owner_id: user.id })
          .select()
          .single()
        if (groupError) throw groupError
        const { error: memberError } = await supabase
          .from('group_members')
          .insert({ group_id: group.id, user_id: user.id, role: 'owner' })
        if (memberError) throw memberError
        groupId = group.id
      }

      if (!threadTitle.trim() || threadTitle.trim().length < 3) throw new Error('Thread title must be at least 3 characters')
      if (!threadBody.trim() || threadBody.trim().length < 10) throw new Error('Thread content must be at least 10 characters')
      if (!commentBody.trim() || commentBody.trim().length < 3) throw new Error('Comment must be at least 3 characters')

      const { data: thread, error: threadError } = await supabase
        .from('threads')
        .insert({
          group_id: groupId,
          author_id: user.id,
          title: threadTitle.trim(),
          body: threadBody.trim(),
          is_hidden: false,
          channel_id: programme.channelId,
          programme_title: programme.title,
          programme_start: programme.start.toISOString(),
          programme_identifier: programmeIdentifier,
        })
        .select()
        .single()
      if (threadError) throw threadError

      const { error: commentError } = await supabase
        .from('comments')
        .insert({
          thread_id: thread.id,
          author_id: user.id,
          body: commentBody.trim(),
          is_hidden: false,
        })
      if (commentError) throw commentError

      return { groupId, threadId: thread.id }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['group-threads', result.groupId] })
      queryClient.invalidateQueries({ queryKey: ['threads-from-my-groups'] })
      queryClient.invalidateQueries({ queryKey: ['latest-threads'] })
      queryClient.invalidateQueries({ queryKey: ['user-groups', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['thread-for-programme'] })
      onSuccess(result.groupId, result.threadId)
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!selectedGroupId && (!groupName.trim() || groupName.trim().length < 3)) {
      setError('Group name must be at least 3 characters')
      return
    }
    if (threadTitle.trim().length < 3) {
      setError('Thread title must be at least 3 characters')
      return
    }
    if (threadBody.trim().length < 10) {
      setError('Thread content must be at least 10 characters')
      return
    }
    if (commentBody.trim().length < 3) {
      setError('Comment must be at least 3 characters')
      return
    }
    setIsSubmitting(true)
    try {
      await createAll.mutateAsync()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create')
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-comment-from-tv-title"
    >
      <div
        className="rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: 'var(--bg-secondary)', border: '2px solid var(--bg-tertiary)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 id="new-comment-from-tv-title" className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
            New comment from TV listing
          </h2>
          <button type="button" onClick={onClose} className="p-2 rounded" aria-label="Close" style={{ color: 'var(--text-primary)' }} disabled={isSubmitting}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block mb-1 font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
              Group
            </label>
            <select
              value={selectedGroupId ?? ''}
              onChange={(e) => setSelectedGroupId(e.target.value ? e.target.value : null)}
              className="w-full px-3 py-2 border-2 rounded text-sm"
              style={{ borderColor: 'var(--bg-tertiary)', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}
              disabled={isSubmitting}
            >
              <option value="">Create new group</option>
              {(userGroups ?? []).map((m) => (
                <option key={m.groups.id} value={m.groups.id}>
                  {m.groups.name}
                </option>
              ))}
            </select>
            {!selectedGroupId && (
              <div className="mt-2 space-y-2">
                <input
                  ref={firstInputRef}
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="Group name"
                  className="w-full px-3 py-2 border-2 rounded text-sm"
                  style={{ borderColor: 'var(--bg-tertiary)', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                  disabled={isSubmitting}
                />
                <input
                  type="text"
                  value={groupDescription}
                  onChange={(e) => setGroupDescription(e.target.value)}
                  placeholder="Brief description (optional)"
                  className="w-full px-3 py-2 border-2 rounded text-sm"
                  style={{ borderColor: 'var(--bg-tertiary)', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                  disabled={isSubmitting}
                />
              </div>
            )}
          </div>

          <div>
            <label className="block mb-1 font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
              Thread title
            </label>
            <input
              type="text"
              value={threadTitle}
              onChange={(e) => setThreadTitle(e.target.value)}
              className="w-full px-3 py-2 border-2 rounded text-sm"
              style={{ borderColor: 'var(--bg-tertiary)', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}
              disabled={isSubmitting}
              minLength={3}
            />
          </div>

          <div>
            <label className="block mb-1 font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
              Thread description
            </label>
            <textarea
              value={threadBody}
              onChange={(e) => setThreadBody(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border-2 rounded text-sm"
              style={{ borderColor: 'var(--bg-tertiary)', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}
              disabled={isSubmitting}
              minLength={10}
            />
          </div>

          <div>
            <label className="block mb-1 font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
              Your comment <span style={{ color: 'var(--color-error)' }}>*</span>
            </label>
            <textarea
              value={commentBody}
              onChange={(e) => setCommentBody(e.target.value)}
              placeholder="Add your comment…"
              rows={4}
              className="w-full px-3 py-2 border-2 rounded text-sm"
              style={{ borderColor: 'var(--bg-tertiary)', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}
              disabled={isSubmitting}
              minLength={3}
            />
          </div>

          {error && (
            <p role="alert" className="text-sm" style={{ color: 'var(--color-error)' }}>
              {error}
            </p>
          )}

          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 font-semibold rounded text-sm" style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 font-semibold rounded text-sm" style={{ backgroundColor: 'var(--bg-interactive)', color: 'var(--text-inverse)' }} disabled={isSubmitting}>
              {isSubmitting ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
